import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import wsService from '@/services/websocket';
import { CaretLeft, TrendUp, Broadcast, DownloadSimple } from '@phosphor-icons/react';
import { BarChart, Bar, ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' },
];

const SalesDashboard = () => {
  const navigate = useNavigate();
  const [totals, setTotals] = useState({ total_orders: 0, total_revenue: 0, avg_order_value: 0, order_type_split: {} });
  const [curve, setCurve] = useState([]);      // cumulative running total, one point per order
  const [feed, setFeed] = useState([]);        // live scrolling list, newest first
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [period, setPeriod] = useState('today');
  const [periodData, setPeriodData] = useState(null);
  const [itemizedData, setItemizedData] = useState(null);
  const [itemizedLoading, setItemizedLoading] = useState(false);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const runningRevenue = useRef(0);
  const runningOrders = useRef(0);
  const countedIds = useRef(new Set());

  const loadInitial = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await apiClient.get('/analytics/dashboard/today', {
        params: { store_id: user.store_id },
      });
      setTotals(res.data);
      runningRevenue.current = res.data.total_revenue;
      runningOrders.current = res.data.total_orders;
      countedIds.current = new Set((res.data.recent_orders || []).map((o) => o.id));
      const seeded = (res.data.recent_orders || []).map((o) => ({
        time: o.time,
        amount: o.total,
        revenue: o.cumulative_revenue,
        orders: o.cumulative_orders,
        label: `#${o.order_number} · ₹${o.total}`,
      }));
      setCurve(seeded);
      setFeed([...(res.data.recent_orders || [])].reverse().map((o) => ({
        order_number: o.order_number, total: o.total, time: o.time, order_type: o.order_type,
      })));
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadPeriod = async (p, startD, endD) => {
    setPeriodLoading(true);
    setItemizedLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const params = { store_id: user.store_id, period: p };
      if (p === 'custom') {
        params.start_date = startD;
        params.end_date = endD;
      }
      const res = await apiClient.get('/analytics/dashboard/period', { params });
      setPeriodData(res.data);
    } catch (e) {
      setPeriodData(null);
    } finally {
      setPeriodLoading(false);
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const params = { store_id: user.store_id, period: p };
      if (p === 'custom') {
        params.start_date = startD;
        params.end_date = endD;
      }
      const res = await apiClient.get('/analytics/dashboard/items', { params });
      setItemizedData(res.data);
    } catch (e) {
      setItemizedData(null);
    } finally {
      setItemizedLoading(false);
    }
  };

  const downloadItemizedCSV = async () => {
    // Downloads the full order-and-item level detail (order date/time,
    // customer, payment method, per-item breakdown) rather than just the
    // aggregated per-item totals shown on screen — this is what's
    // actually useful for real business decisions, not just a quick
    // glance at top sellers.
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const params = { store_id: user.store_id, period };
      if (period === 'custom') {
        params.start_date = customStart;
        params.end_date = customEnd;
      }
      const res = await apiClient.get('/analytics/export/orders-csv', { params, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `orders_detailed_${period}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not download the report. Try again.');
    }
  };

  const downloadExcludedCSV = async () => {
    // The counterpart — orders deliberately left OUT of every revenue
    // figure on this dashboard (cancelled, or never actually paid).
    // Nothing should silently vanish just because it's correctly
    // excluded from revenue — this is how staff can still see and
    // follow up on it.
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const params = { store_id: user.store_id, period };
      if (period === 'custom') {
        params.start_date = customStart;
        params.end_date = customEnd;
      }
      const res = await apiClient.get('/analytics/export/excluded-orders-csv', { params, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `excluded_orders_${period}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not download the report. Try again.');
    }
  };

  const downloadReport = async (format) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const params = { store_id: user.store_id, period };
      if (period === 'custom') {
        params.start_date = customStart;
        params.end_date = customEnd;
      }
      const res = await apiClient.get(`/analytics/dashboard/export/${format}`, {
        params,
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `sales_report.${format}`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not download the report. Try again.');
    }
  };

  useEffect(() => {
    if (period === 'today') {
      // Today's live view has its own order-by-order data already; just
      // fetch the item-level breakdown separately, since that's naturally
      // aggregated over the day rather than per-order.
      (async () => {
        setItemizedLoading(true);
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const res = await apiClient.get('/analytics/dashboard/items', {
            params: { store_id: user.store_id, period: 'today' },
          });
          setItemizedData(res.data);
        } catch (e) {
          setItemizedData(null);
        } finally {
          setItemizedLoading(false);
        }
      })();
    } else if (period !== 'custom') {
      loadPeriod(period);
    }
  }, [period]);

  useEffect(() => {
    let cancelled = false;
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Post Pay orders count the moment they're placed — food is already
    // committed, "as good as money in". Prepaid orders only count once
    // payment_status genuinely flips to PAID, so a cancelled payment
    // attempt never inflates today's numbers. countedIds (seeded from the
    // initial load, shared across both listeners) guards against any
    // order ever being counted twice.
    const countOrder = (order) => {
      if (!order || countedIds.current.has(order.id)) return;
      countedIds.current.add(order.id);

      const total = Number(order.total || 0);
      runningRevenue.current = Math.round((runningRevenue.current + total) * 100) / 100;
      runningOrders.current += 1;

      const now = new Date();
      const timeLabel = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

      setCurve((prev) => [...prev, {
        time: timeLabel,
        amount: total,
        revenue: runningRevenue.current,
        orders: runningOrders.current,
        label: `#${order.order_number} · ₹${total}`,
      }]);

      setFeed((prev) => [{
        order_number: order.order_number, total, time: timeLabel, order_type: order.order_type,
        is_post_pay: !!order.is_post_pay,
      }, ...prev].slice(0, 30));

      setTotals((prev) => ({
        ...prev,
        total_orders: runningOrders.current,
        total_revenue: runningRevenue.current,
        avg_order_value: Math.round((runningRevenue.current / runningOrders.current) * 100) / 100,
      }));

      // Keep the itemized table live too — it otherwise only loaded once
      // on mount and would silently go stale as new orders came in
      // (confirmed live: two real orders placed after page load never
      // showed up here even though the totals/feed above updated fine).
      if (Array.isArray(order.items) && order.items.length > 0) {
        setItemizedData((prev) => {
          const base = prev || { period: 'today', total_items_sold: 0, total_unique_items: 0, items: [] };
          const itemsByName = {};
          for (const it of base.items) itemsByName[it.name] = { ...it };
          for (const item of order.items) {
            const name = item.menu_item_name;
            if (!name) continue;
            const qty = Number(item.qty || 0);
            const lineRevenue = Number(item.price || 0) * qty;
            if (!itemsByName[name]) {
              itemsByName[name] = { name, total_quantity: 0, total_revenue: 0, order_count: 0, avg_price: 0 };
            }
            itemsByName[name].total_quantity += qty;
            itemsByName[name].total_revenue = Math.round((itemsByName[name].total_revenue + lineRevenue) * 100) / 100;
            itemsByName[name].order_count += 1;
            itemsByName[name].avg_price = itemsByName[name].total_quantity
              ? Math.round((itemsByName[name].total_revenue / itemsByName[name].total_quantity) * 100) / 100
              : 0;
          }
          const items = Object.values(itemsByName).sort((a, b) => b.total_quantity - a.total_quantity);
          return {
            ...base,
            items,
            total_unique_items: items.length,
            total_items_sold: items.reduce((s, i) => s + i.total_quantity, 0),
          };
        });
      }
    };

    const handleOrderCreated = (order) => {
      if (order?.is_post_pay) countOrder(order);
    };
    const handleOrderUpdated = (order) => {
      if (order && (order.payment_status || '').toUpperCase() === 'PAID') countOrder(order);
    };

    (async () => {
      await loadInitial();
      if (cancelled) return;
      wsService.connect(`pos:store:${user.store_id}`);
      wsService.onConnect(() => setConnected(true));
      wsService.on('order.created', handleOrderCreated);
      wsService.on('order.updated', handleOrderUpdated);
    })();

    return () => {
      cancelled = true;
      wsService.off('order.created', handleOrderCreated);
      wsService.off('order.updated', handleOrderUpdated);
      wsService.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#1c1310] text-[#fbf6ee] pb-8">
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate('/pos-dashboard')} className="p-1">
          <CaretLeft size={22} />
        </button>
        <TrendUp size={20} weight="bold" className="text-[#d4a24c]" />
        <h1 className="text-lg font-bold">Sales</h1>
        {period === 'today' && (
          <span className={`ml-auto flex items-center gap-1 text-[11px] font-semibold ${connected ? 'text-[#9fc48f]' : 'text-[#fbf6ee]/40'}`}>
            <Broadcast size={14} weight="bold" />
            {connected ? 'Live' : 'Connecting...'}
          </span>
        )}
      </div>

      <div className="px-5 flex gap-1.5 mb-4 overflow-x-auto">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              period === p.id ? 'bg-[#d4a24c] text-[#241a12]' : 'bg-white/[0.05] text-[#fbf6ee]/60'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="px-5">
        {period === 'today' ? (
          <>
            {loading && <p className="text-sm text-[#fbf6ee]/40 text-center py-10">Loading...</p>}
            {error && <p className="text-sm text-[#e8836b] text-center py-10">{error}</p>}

            {!loading && !error && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-2xl p-4 bg-gradient-to-br from-[#3a2a1a] to-[#241a12] border border-[#d4a24c]/25">
                    <div className="text-[11px] uppercase tracking-wide font-bold text-[#d4a24c]/70 mb-2">Orders so far</div>
                <div className="text-2xl font-bold text-[#d4a24c]" data-testid="sd-total-orders">{totals.total_orders}</div>
              </div>
              <div className="rounded-2xl p-4 bg-gradient-to-br from-[#2e1f1c] to-[#1e1512] border border-[#c4432b]/30">
                <div className="text-[11px] uppercase tracking-wide font-bold text-[#c4432b]/70 mb-2">Revenue so far</div>
                <div className="text-2xl font-bold text-[#e8836b]" data-testid="sd-total-revenue">₹{totals.total_revenue.toLocaleString('en-IN')}</div>
              </div>
              <div className="col-span-2 rounded-2xl p-4 bg-gradient-to-br from-[#28311f] to-[#1a2214] border border-[#4a6741]/35">
                <div className="text-[11px] uppercase tracking-wide font-bold text-[#9fc48f]/80 mb-2">Avg order value</div>
                <div className="text-2xl font-bold text-[#9fc48f]" data-testid="sd-avg-order">₹{totals.avg_order_value.toLocaleString('en-IN')}</div>
              </div>
            </div>

            <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 mb-4">
              <h3 className="text-[13px] text-[#fbf6ee]/60 mb-3">Revenue as it happens</h3>
              {curve.length === 0 ? (
                <p className="text-xs text-[#fbf6ee]/30 text-center py-8">No orders yet today.</p>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={curve}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'rgba(251,246,238,0.4)' }} minTickGap={30} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'rgba(251,246,238,0.4)' }} width={45} />
                    <Tooltip
                      contentStyle={{ background: '#241a12', border: '1px solid rgba(212,162,76,0.3)', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: '#fbf6ee' }}
                      formatter={(v) => [`₹${v}`, 'Order amount']}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ''}
                    />
                    <Bar dataKey="amount" fill="#d4a24c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 mb-4">
              <h3 className="text-[13px] text-[#fbf6ee]/60 mb-2">Live order feed</h3>
              {feed.length === 0 ? (
                <p className="text-xs text-[#fbf6ee]/30 text-center py-6">Waiting for the first order...</p>
              ) : (
                <div className="space-y-0.5 max-h-64 overflow-y-auto">
                  {feed.map((o, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/[0.06] last:border-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-sm">#{o.order_number}</span>
                        <span className="text-[#fbf6ee]/40 text-xs truncate">{o.order_type} · {o.time}</span>
                        {o.is_post_pay && (
                          <span className="bg-[#d4a24c]/15 text-[#d4a24c] text-[10px] px-2 py-0.5 rounded flex-shrink-0">Post pay</span>
                        )}
                      </div>
                      <span className="font-bold text-[#9fc48f] flex-shrink-0">₹{o.total}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <ItemizedReportTable itemizedData={itemizedData} itemizedLoading={itemizedLoading} onDownload={downloadItemizedCSV} onDownloadExcluded={downloadExcludedCSV} />

            <div className="grid grid-cols-3 gap-2">
              {Object.entries(totals.order_type_split || {}).map(([type, revenue]) => (
                <div key={type} className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3 text-center">
                  <div className="text-sm font-bold">₹{Number(revenue).toLocaleString('en-IN')}</div>
                  <div className="text-[10px] text-[#fbf6ee]/40 mt-0.5">{type.replace('_', ' ')}</div>
                </div>
              ))}
            </div>

            <p className="text-[10px] text-[#fbf6ee]/30 text-center pt-4">Updates live as orders come in · Cancelled orders excluded</p>
              </>
            )}
          </>
        ) : (
          <>
            {period === 'custom' && (
              <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 mb-4">
                <h3 className="text-[13px] text-[#fbf6ee]/60 mb-3">Pick a date range</h3>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="flex-1 bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-sm text-[#fbf6ee]"
                    style={{ colorScheme: 'dark' }}
                  />
                  <span className="text-[#fbf6ee]/40 text-xs">to</span>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="flex-1 bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-sm text-[#fbf6ee]"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
                <button
                  onClick={() => customStart && customEnd && loadPeriod('custom', customStart, customEnd)}
                  disabled={!customStart || !customEnd}
                  className="w-full mt-3 py-2 rounded-lg bg-[#d4a24c] text-[#241a12] text-sm font-semibold disabled:opacity-40"
                >
                  Show report
                </button>
              </div>
            )}
            {periodLoading && <p className="text-sm text-[#fbf6ee]/40 text-center py-10">Loading...</p>}
            {!periodLoading && periodData && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-2xl p-4 bg-gradient-to-br from-[#3a2a1a] to-[#241a12] border border-[#d4a24c]/25">
                    <div className="text-[11px] uppercase tracking-wide font-bold text-[#d4a24c]/70 mb-2">Orders</div>
                    <div className="text-2xl font-bold text-[#d4a24c]" data-testid="sd-period-orders">{periodData.total_orders}</div>
                  </div>
                  <div className="rounded-2xl p-4 bg-gradient-to-br from-[#2e1f1c] to-[#1e1512] border border-[#c4432b]/30">
                    <div className="text-[11px] uppercase tracking-wide font-bold text-[#c4432b]/70 mb-2">Revenue</div>
                    <div className="text-2xl font-bold text-[#e8836b]" data-testid="sd-period-revenue">₹{periodData.total_revenue.toLocaleString('en-IN')}</div>
                  </div>
                  <div className="col-span-2 rounded-2xl p-4 bg-gradient-to-br from-[#28311f] to-[#1a2214] border border-[#4a6741]/35">
                    <div className="text-[11px] uppercase tracking-wide font-bold text-[#9fc48f]/80 mb-2">Avg order value</div>
                    <div className="text-2xl font-bold text-[#9fc48f]">₹{periodData.avg_order_value.toLocaleString('en-IN')}</div>
                  </div>
                </div>

                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => downloadReport('xlsx')}
                    className="flex-1 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.1] text-sm font-semibold"
                  >
                    Download Excel
                  </button>
                  <button
                    onClick={() => downloadReport('pdf')}
                    className="flex-1 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.1] text-sm font-semibold"
                  >
                    Download PDF
                  </button>
                </div>

                <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 mb-4">
                  <h3 className="text-[13px] text-[#fbf6ee]/60 mb-3">Revenue by {periodData.granularity === 'month' ? 'month' : 'day'} — full period</h3>
                  {periodData.daily.length === 0 ? (
                    <p className="text-xs text-[#fbf6ee]/30 text-center py-8">No orders in this period.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <ComposedChart data={periodData.daily}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgba(251,246,238,0.4)' }}
                               tickFormatter={(d) => (periodData.granularity === 'month' ? d : d.slice(5))}
                               interval={periodData.granularity === 'month' ? 0 : 'preserveStartEnd'} minTickGap={20} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'rgba(251,246,238,0.4)' }} width={45} />
                        <Tooltip
                          contentStyle={{ background: '#241a12', border: '1px solid rgba(212,162,76,0.3)', borderRadius: 8, fontSize: 12 }}
                          labelStyle={{ color: '#fbf6ee' }}
                          formatter={(v) => [`₹${v}`, 'Revenue']}
                        />
                        <Bar dataKey="revenue" fill="#d4a24c" fillOpacity={0.5} radius={[4, 4, 0, 0]} />
                        <Line type="monotone" dataKey="revenue" stroke="#e8836b" strokeWidth={2} dot={{ r: 3, fill: '#e8836b' }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <ItemizedReportTable itemizedData={itemizedData} itemizedLoading={itemizedLoading} onDownload={downloadItemizedCSV} onDownloadExcluded={downloadExcludedCSV} />

                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(periodData.order_type_split || {}).map(([type, revenue]) => (
                    <div key={type} className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3 text-center">
                      <div className="text-sm font-bold">₹{Number(revenue).toLocaleString('en-IN')}</div>
                      <div className="text-[10px] text-[#fbf6ee]/40 mt-0.5">{type.replace('_', ' ')}</div>
                    </div>
                  ))}
                </div>

                <p className="text-[10px] text-[#fbf6ee]/30 text-center pt-4">
                  {periodData.range_start} to {periodData.range_end} · Pending payment and cancelled orders excluded
                </p>
              </>
            )}
            {!periodLoading && !periodData && (
              <p className="text-sm text-[#e8836b] text-center py-10">Failed to load this period.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Itemized sales table — same period/accuracy rule as the rest of this
// dashboard, shared between the live "Today" view and the historical
// period view so it only needs to be built once.
const ItemizedReportTable = ({ itemizedData, itemizedLoading, onDownload, onDownloadExcluded }) => {
  if (itemizedLoading) {
    return <p className="text-sm text-[#fbf6ee]/40 text-center py-8">Loading items...</p>;
  }
  if (!itemizedData || itemizedData.items.length === 0) {
    return <p className="text-xs text-[#fbf6ee]/30 text-center py-8">No items sold in this period.</p>;
  }
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 mb-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-[13px] text-[#fbf6ee]/60">
          Itemised report · {itemizedData.total_items_sold} items · {itemizedData.total_unique_items} dishes
        </h3>
        <div className="flex items-center gap-3">
          <button
            onClick={onDownload}
            className="flex items-center gap-1 text-[11px] font-semibold text-[#d4a24c] hover:text-[#e8b666]"
            data-testid="sd-download-items-csv"
          >
            <DownloadSimple size={14} weight="bold" />
            Detailed CSV
          </button>
          <button
            onClick={onDownloadExcluded}
            className="flex items-center gap-1 text-[11px] font-semibold text-[#fbf6ee]/50 hover:text-[#e8836b]"
            data-testid="sd-download-excluded-csv"
            title="Cancelled orders and orders that were never paid — excluded from every revenue figure above"
          >
            <DownloadSimple size={14} weight="bold" />
            Excluded orders
          </button>
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[#fbf6ee]/40 text-left border-b border-white/[0.06]">
              <th className="pb-2 font-medium">Item</th>
              <th className="pb-2 font-medium text-right">Qty</th>
              <th className="pb-2 font-medium text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {itemizedData.items.map((item, i) => (
              <tr key={i} className="border-b border-white/[0.04] last:border-0">
                <td className="py-2 text-[#fbf6ee] truncate max-w-[160px]">{item.name}</td>
                <td className="py-2 text-right text-[#fbf6ee]/70">{item.total_quantity}</td>
                <td className="py-2 text-right font-semibold text-[#9fc48f]">₹{item.total_revenue.toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesDashboard;
